import type { TextBlock } from '../interfaces/textEditor.interface';

/**
 * Normalize the Formik `values` a block editor receives into a flat `TextBlock[]`.
 *
 * TRIL-204 left two shapes in play: AddPost wraps this form with a flat `TextBlock[]`, while
 * EditDraft and EditPostReplyDraft wrap it with `{ blocks: TextBlock[] }`. `AddPostFormRender`
 * was given a local normalization at the time; its two siblings were not, and they read
 * `values.length` directly in their move handlers.
 *
 * TRIL-614 is what that cost. On the object shape `values.length` is `undefined`, so
 * "move down" computed `Math.min(undefined - 1, index + 1)` → `NaN`, and Formik's `move` does
 * `splice(to, 0, …)` — **splice coerces NaN to 0**, inserting the block at the TOP:
 *
 *     before: A,B,C     move B down     after: B,A,C
 *
 * For a block at index 1 that is indistinguishable from "move up", which is how it was reported.
 * The same `undefined` also made `disabled={values.length === 1}` always false.
 *
 * Shared rather than copied a third time: three components needing the same normalization, two of
 * them silently wrong, is the argument for one producer (the TRIL-608 lesson).
 */
export function toBlockArray(values: unknown): TextBlock[] {
  if (Array.isArray(values)) return values as TextBlock[];
  // The unwrapped `blocks` is CHECKED too. Returning it unverified reproduced this ticket's own
  // bug one level down: `{ blocks: { a: 1 } }` yielded a non-array whose `.length` is undefined,
  // so moveDownTarget went back to NaN — the exact failure, sourced from a junk field instead of
  // a wrapper. A function that promises an array has to return one.
  const unwrapped = (values as { blocks?: unknown } | null | undefined)?.blocks;
  return Array.isArray(unwrapped) ? (unwrapped as TextBlock[]) : [];
}

/**
 * Destination index for "move block down", clamped to the end of the list.
 *
 * Takes the RAW Formik `values` and normalizes internally, deliberately. Typing the parameter as
 * `TextBlock[]` looked safer but was not: `values` is `any` in these components, so passing it
 * unnormalized still type-checked — the exact original mistake, still available. Owning the
 * normalization here removes the footgun instead of documenting it.
 */
export function moveDownTarget(values: unknown, index: number): number {
  // Floor at 0. An empty list made this -1, and `splice(-1, 0, x)` inserts BEFORE the last
  // element rather than at the end — a second silently-wrong destination in the helper whose
  // entire purpose is to stop producing those. Unreachable today; the helper still shouldn't
  // emit an index it would be wrong to use.
  return Math.max(0, Math.min(toBlockArray(values).length - 1, index + 1));
}

/** Destination index for "move block up", clamped to the start. */
export function moveUpTarget(index: number): number {
  return Math.max(0, index - 1);
}
